import React from 'react';

import { BodyText } from './BodyText/BodyText';
import Element from './Element/Element';
import Layout from './layout';
import SEO from './seo';
import { SubHeader } from './SubHeader/SubHeader';

const ENGLISH_CONTENT = {
  title: 'Full Stack Open is migrating to a new platform',
  paragraphs: [
    `The Full Stack Open course is migrating to a new platform (<a href="https://courses.mooc.fi" target="_blank" rel="noreferrer">courses.mooc.fi</a>). Here is what you need to know:`,

    `<strong>Parts 0–5:</strong> for now, still uses the old system (<a href="https://studies.cs.helsinki.fi/stats" target="_blank" rel="noreferrer">studies.cs.helsinki.fi/stats</a>) and materials on this website. You upload your solutions to GitHub and paste the link into that system. Parts 0–5 will also migrate to the new platform in 2027.`,

    `<strong>Parts 6 onwards:</strong> Full Stack Open is migrating to a new platform and the old system is no longer in use. For now, the new platform is only available in English. One repository per part is strongly recommended, with the indicated structure and the necessary automated tests. Passing the tests is mandatory. In the last exercise of each part, you submit the repository URL for review.`,

    `If you have already submitted the parts in the old system, you do not need to resubmit them in the new system.`,

    `To continue with a new part: use the new platform starting from that part (all links are in the <a href="https://fullstackopen.com/en/">English version of the course</a> from part 6 onwards).`,

    `<strong>Credits:</strong> parts 0–5 are obtained together through an exam. From part 6 onwards, each part grants 1 ECTS individually. There is no &ldquo;single 14 ECTS credit&rdquo; for the entire course.`,

    `<strong>Exam:</strong> it is recommended to take the exam as soon as possible to get the credits you already have registered in the previous exercise submission system, and then continue with the following parts on the new platform.`,
  ],
};

const TRANSLATED_CONTENT = {
  fi: {
    machineTranslatedNote: 'Konekäännetty suomeksi.',
    title: 'Full Stack Open siirtyy uudelle alustalle',
    paragraphs: [
      `Full Stack Open -kurssi siirtyy uudelle alustalle (<a href="https://courses.mooc.fi" target="_blank" rel="noreferrer">courses.mooc.fi</a>). Tässä on, mitä sinun tulee tietää:`,

      `<strong>Osat 0–5:</strong> toistaiseksi käytössä on edelleen vanha järjestelmä (<a href="https://studies.cs.helsinki.fi/stats" target="_blank" rel="noreferrer">studies.cs.helsinki.fi/stats</a>) ja tämän sivuston materiaalit. Lataat ratkaisusi GitHubiin ja liität linkin kyseiseen järjestelmään. Myös osat 0–5 siirtyvät uudelle alustalle vuonna 2027.`,

      `<strong>Osat 6 eteenpäin:</strong> Full Stack Open siirtyy uudelle alustalle, eikä vanha järjestelmä ole enää käytössä. Toistaiseksi uusi alusta on saatavilla vain englanniksi. Suositeltavaa on käyttää yhtä repositoriota per osa, ohjeiden mukaisella rakenteella ja tarvittavilla automaattisilla testeillä. Testien läpäiseminen on pakollista. Kunkin osan viimeisessä tehtävässä palautat repositorion URL-osoitteen arvioitavaksi.`,

      `Jos olet jo palauttanut osat vanhassa järjestelmässä, sinun ei tarvitse palauttaa niitä uudelleen uudessa järjestelmässä.`,

      `Jatkaaksesi uuteen osaan: käytä uutta alustaa kyseisestä osasta lähtien (kaikki linkit löytyvät kurssin <a href="https://fullstackopen.com/en/">englanninkielisestä versiosta</a> osasta 6 alkaen).`,

      `<strong>Opintopisteet:</strong> osat 0–5 suoritetaan yhdessä tentin kautta. Osasta 6 alkaen jokainen osa antaa 1 opintopisteen erikseen. Koko kurssista ei enää saa yhtä 14 opintopisteen kokonaisuutta.`,

      `<strong>Tentti:</strong> suosittelemme suorittamaan tentin mahdollisimman pian, jotta saat hyväksi jo aiemmin vanhaan palautusjärjestelmään kirjatut suoritukset, ja jatkat sen jälkeen seuraavien osien opiskelua uudella alustalla.`,
    ],
  },
  es: {
    machineTranslatedNote: null,
    showOriginalFallback: false,
    title: 'Full Stack Open se está trasladando a una nueva plataforma',
    paragraphs: [
      `El curso Full Stack Open se está trasladando a una nueva plataforma (<a href="https://courses.mooc.fi" target="_blank" rel="noreferrer">courses.mooc.fi</a>). Esto es lo que necesitas saber:`,

      `<strong>Partes 0–5:</strong> por ahora, se sigue utilizando el sistema antiguo (<a href="https://studies.cs.helsinki.fi/stats" target="_blank" rel="noreferrer">studies.cs.helsinki.fi/stats</a>) y los materiales de este sitio web. Debes subir tus soluciones a GitHub y pegar el enlace en ese sistema. Las partes 0–5 también migrarán a la nueva plataforma en 2027.`,

      `<strong>Partes 6 en adelante:</strong> Full Stack Open se está trasladando a una nueva plataforma y el sistema antiguo ya no está en uso. Por ahora, la nueva plataforma solo está disponible en inglés. Se recomienda encarecidamente usar un repositorio por parte, con la estructura indicada y los tests automáticos necesarios. Pasar los tests es obligatorio. En el último ejercicio de cada parte, envías la URL del repositorio para su revisión.`,

      `Si ya has enviado las partes en el sistema antiguo, no necesitas volver a enviarlas en el sistema nuevo.`,

      `Para continuar con una parte nueva: usa la nueva plataforma a partir de esa parte (todos los enlaces están en la <a href="https://fullstackopen.com/en/">versión en inglés del curso</a> a partir de la parte 6).`,

      `<strong>Créditos:</strong> las partes 0–5 se obtienen conjuntamente mediante un examen. A partir de la parte 6, cada parte otorga 1 ECTS de forma individual. Ya no existe un único crédito de 14 ECTS para todo el curso.`,

      `<strong>Examen:</strong> se recomienda realizar el examen lo antes posible para obtener los créditos ya registrados en el sistema de envío de ejercicios anterior, y luego continuar con las siguientes partes en la nueva plataforma.`,
    ],
  },
  fr: {
    machineTranslatedNote: 'Traduction automatique en français.',
    title: 'Full Stack Open migre vers une nouvelle plateforme',
    paragraphs: [
      `Le cours Full Stack Open migre vers une nouvelle plateforme (<a href="https://courses.mooc.fi" target="_blank" rel="noreferrer">courses.mooc.fi</a>). Voici ce que vous devez savoir :`,

      `<strong>Parties 0–5 :</strong> pour l'instant, l'ancien système (<a href="https://studies.cs.helsinki.fi/stats" target="_blank" rel="noreferrer">studies.cs.helsinki.fi/stats</a>) et les supports de ce site sont toujours utilisés. Vous téléversez vos solutions sur GitHub et collez le lien dans ce système. Les parties 0–5 migreront également vers la nouvelle plateforme en 2027.`,

      `<strong>Parties 6 et suivantes :</strong> Full Stack Open migre vers une nouvelle plateforme et l'ancien système n'est plus utilisé. Pour l'instant, la nouvelle plateforme n'est disponible qu'en anglais. Il est fortement recommandé d'utiliser un dépôt par partie, avec la structure indiquée et les tests automatisés nécessaires. Réussir les tests est obligatoire. Dans le dernier exercice de chaque partie, vous soumettez l'URL du dépôt pour évaluation.`,

      `Si vous avez déjà soumis les parties dans l'ancien système, vous n'avez pas besoin de les soumettre à nouveau dans le nouveau système.`,

      `Pour continuer avec une nouvelle partie : utilisez la nouvelle plateforme à partir de cette partie (tous les liens se trouvent dans la <a href="https://fullstackopen.com/en/">version anglaise du cours</a> à partir de la partie 6).`,

      `<strong>Crédits :</strong> les parties 0–5 s'obtiennent ensemble via un examen. À partir de la partie 6, chaque partie accorde 1 crédit ECTS individuellement. Il n'y a pas de crédit unique de 14 ECTS pour l'ensemble du cours.`,

      `<strong>Examen :</strong> il est recommandé de passer l'examen dès que possible afin d'obtenir les crédits déjà enregistrés dans l'ancien système de soumission des exercices, puis de continuer avec les parties suivantes sur la nouvelle plateforme.`,
    ],
  },
  zh: {
    machineTranslatedNote: '本内容为机器翻译。',
    title: '全栈公开课正在迁移到新平台',
    paragraphs: [
      `全栈公开课正在迁移到新平台(<a href="https://courses.mooc.fi" target="_blank" rel="noreferrer">courses.mooc.fi</a>)。以下是您需要了解的内容:`,

      `<strong>第 0–5 部分:</strong>目前仍使用旧系统(<a href="https://studies.cs.helsinki.fi/stats" target="_blank" rel="noreferrer">studies.cs.helsinki.fi/stats</a>)以及本网站的资料。您需要将解答上传到 GitHub,并将链接粘贴到该系统中。第 0–5 部分也将于 2027 年迁移到新平台。`,

      `<strong>第 6 部分及以后:</strong>全栈公开课正在迁移到新平台,旧系统已不再使用。目前,新平台仅提供英文版本。强烈建议每个部分使用一个仓库,并按照指定的结构编写必要的自动化测试。通过测试是必须的。在每个部分的最后一个练习中,您需要提交仓库的 URL 以供审核。`,

      `如果您已经在旧系统中提交过相应部分,则无需在新系统中重新提交。`,

      `要继续学习新的部分:请从该部分开始使用新平台(从第 6 部分起,所有链接都在课程的<a href="https://fullstackopen.com/en/">英文版本</a>中)。`,

      `<strong>学分:</strong>第 0–5 部分通过一次考试共同获得。从第 6 部分开始,每个部分单独授予 1 个 ECTS 学分。整个课程不再有单一的 14 ECTS 学分。`,

      `<strong>考试:</strong>建议尽快参加考试,以获得您在之前的练习提交系统中已登记的学分,然后在新平台上继续学习后续部分。`,
    ],
  },
  ptbr: {
    machineTranslatedNote: 'Traduzido automaticamente para o português (BR).',
    title: 'O Full Stack Open está migrando para uma nova plataforma',
    paragraphs: [
      `O curso Full Stack Open está migrando para uma nova plataforma (<a href="https://courses.mooc.fi" target="_blank" rel="noreferrer">courses.mooc.fi</a>). Aqui está o que você precisa saber:`,

      `<strong>Partes 0–5:</strong> por enquanto, ainda são usados o sistema antigo (<a href="https://studies.cs.helsinki.fi/stats" target="_blank" rel="noreferrer">studies.cs.helsinki.fi/stats</a>) e os materiais deste site. Você envia suas soluções para o GitHub e cola o link nesse sistema. As partes 0–5 também migrarão para a nova plataforma em 2027.`,

      `<strong>Partes 6 em diante:</strong> o Full Stack Open está migrando para uma nova plataforma e o sistema antigo não está mais em uso. Por enquanto, a nova plataforma está disponível apenas em inglês. É altamente recomendado usar um repositório por parte, com a estrutura indicada e os testes automatizados necessários. Passar nos testes é obrigatório. No último exercício de cada parte, você envia a URL do repositório para revisão.`,

      `Se você já enviou as partes no sistema antigo, não precisa reenviá-las no novo sistema.`,

      `Para continuar com uma nova parte: use a nova plataforma a partir dessa parte (todos os links estão na <a href="https://fullstackopen.com/en/">versão em inglês do curso</a> a partir da parte 6).`,

      `<strong>Créditos:</strong> as partes 0–5 são obtidas em conjunto por meio de uma prova. A partir da parte 6, cada parte concede 1 ECTS individualmente. Não existe mais um único crédito de 14 ECTS para o curso inteiro.`,

      `<strong>Prova:</strong> recomenda-se fazer a prova o quanto antes para obter os créditos já registrados no sistema de envio de exercícios anterior, e então continuar com as partes seguintes na nova plataforma.`,
    ],
  },
};

const noteStyle = {
  marginBottom: 8,
  fontStyle: 'italic',
};

const dividerStyle = {
  margin: '2.5em 0',
  border: 0,
  borderTop: '1px solid #ccc',
};

const NewPlatformInfoPage = ({ lang, title }) => {
  const translated = TRANSLATED_CONTENT[lang];

  return (
    <Layout>
      <SEO
        lang={lang}
        title={title}
        description="What you need to know about the new Full Stack Open exercise submission system for parts 6 onwards."
      />

      <Element className="container link spacing spacing--after">
        {translated ? (
          <>
            {translated.machineTranslatedNote && (
              <p style={noteStyle}>{translated.machineTranslatedNote}</p>
            )}

            <SubHeader
              className="spacing--after-small"
              headingLevel="h1"
              text={translated.title}
            />

            <BodyText text={translated.paragraphs} />

            {translated.showOriginalFallback !== false && (
              <>
                <hr style={dividerStyle} />

                <p style={noteStyle}>
                  Original English text (precautionary reference in case the
                  translation above is unclear):
                </p>

                <SubHeader
                  className="spacing--after-small"
                  headingLevel="h2"
                  text={ENGLISH_CONTENT.title}
                />

                <BodyText text={ENGLISH_CONTENT.paragraphs} />
              </>
            )}
          </>
        ) : (
          <>
            <SubHeader
              className="spacing--after-small"
              headingLevel="h1"
              text={ENGLISH_CONTENT.title}
            />

            <BodyText text={ENGLISH_CONTENT.paragraphs} />
          </>
        )}
      </Element>
    </Layout>
  );
};

export default NewPlatformInfoPage;
